import React, { useState, useEffect } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { NAV_LINKS, Button } from "@/components/UI";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-ccpm-ivory/85 backdrop-blur-xl border-b border-ccpm-border" : "bg-transparent"}`} data-testid="site-header">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between h-24">
          <Link to="/" data-testid="nav-logo" aria-label="CCPM Home"><Wordmark /></Link>

          <nav className="hidden xl:flex items-center gap-5" data-testid="desktop-nav">
            {NAV_LINKS.filter((l) => l.to !== "/registry").map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`}
                className={({ isActive }) => `text-[13px] font-medium tracking-wide whitespace-nowrap transition-colors duration-200 ${isActive ? "text-ccpm-olive" : "text-ccpm-charcoal hover:text-ccpm-olive"}`}>
                {l.short || l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:block">
            <Button to="/registry/apply" variant="primary" className="!px-5 !py-2.5 !text-[13px]" data-testid="nav-cta-register">Register Interest</Button>
          </div>

          <button className="xl:hidden text-ccpm-charcoal" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-open">
            <Menu strokeWidth={1.5} size={28} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[60] bg-ccpm-charcoal text-ccpm-ivory xl:hidden" data-testid="mobile-nav"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
            <div className="flex items-center justify-between h-20 px-6">
              <Wordmark light />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close"><X strokeWidth={1.5} size={28} /></button>
            </div>
            <nav className="px-6 mt-8 flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <Link to={l.to} className="block py-4 text-2xl font-serif border-b border-white/10" data-testid={`mobile-nav-${l.to.slice(1)}`}>{l.label}</Link>
                </motion.div>
              ))}
              <Button to="/registry/apply" variant="light" className="mt-8">Register Interest</Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
